import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ChevronRight, MessageCircle, ShoppingBag, Sparkles } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import apiClient, { resolveImageUrl } from "@/utils/api";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  confirmed: "bg-blue-100 text-blue-800 border-blue-300",
  processing: "bg-indigo-100 text-indigo-800 border-indigo-300",
  shipped: "bg-purple-100 text-purple-800 border-purple-300",
  delivered: "bg-emerald-100 text-emerald-800 border-emerald-300",
  cancelled: "bg-rose-100 text-rose-800 border-rose-300",
};

export default function MyOrders() {
  const { customer, loading, isSignedIn, signInWithGoogle } = useCustomerAuth();
  const [orders, setOrders] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!isSignedIn) return;
    (async () => {
      setFetching(true);
      try {
        const { data } = await apiClient.get("/customer/orders");
        setOrders(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setFetching(false);
      }
    })();
  }, [isSignedIn]);

  if (loading) return <div className="text-center py-24 text-stone-500 text-sm">Loading…</div>;

  if (!isSignedIn) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-4">
        <h1 className="font-heading text-2xl font-bold text-stone-900">Sign in to see your orders</h1>
        <button
          type="button"
          onClick={signInWithGoogle}
          data-testid="orders-signin-google-btn"
          className="mx-auto flex items-center gap-3 px-6 py-3 bg-white border border-stone-300 hover:border-[#D4AF37] rounded-xl shadow-sm font-semibold text-sm text-stone-800"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
          <span>Continue with Google</span>
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#996515] text-[10px] font-bold uppercase tracking-widest mb-1">
            <Sparkles className="w-3 h-3" />
            <span>{customer.email}</span>
          </div>
          <h1 data-testid="my-orders-title" className="font-heading text-3xl font-bold text-stone-900">
            My Orders
          </h1>
        </div>
        <Link
          to="/shop"
          className="px-4 py-2 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl transition inline-flex items-center gap-1.5"
        >
          <ShoppingBag className="w-4 h-4" />
          Continue Shopping
        </Link>
      </div>

      {fetching ? (
        <div className="grid grid-cols-1 gap-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-24 bg-stone-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div data-testid="my-orders-empty" className="bg-white rounded-3xl border border-[#E5E0D8] p-10 text-center space-y-3">
          <Package className="w-10 h-10 text-stone-300 mx-auto" />
          <h2 className="font-heading font-bold text-lg text-stone-900">No orders yet</h2>
          <p className="text-xs text-stone-500 max-w-sm mx-auto">
            Once you place your first order, it will show up here with real-time status tracking.
          </p>
          <Link
            to="/shop"
            className="inline-block px-5 py-2 bg-[#D4AF37] hover:bg-[#996515] hover:text-white text-[#1C1917] text-xs font-bold rounded-xl transition"
          >
            Browse Laces
          </Link>
        </div>
      ) : (
        <ul data-testid="my-orders-list" className="space-y-3">
          {orders.map((o) => {
            const status = o.order_status || "pending";
            const statusClass = STATUS_STYLES[status] || STATUS_STYLES.pending;
            return (
              <li
                key={o.order_number}
                data-testid={`my-order-${o.order_number}`}
                className="bg-white rounded-2xl border border-[#E5E0D8] p-4 sm:p-5 shadow-sm hover:shadow-md transition"
              >
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* thumb */}
                  <div className="flex -space-x-2 shrink-0">
                    {(o.items || []).slice(0, 3).map((it, i) => (
                      <img
                        key={i}
                        src={resolveImageUrl(it.image) || "https://via.placeholder.com/60"}
                        alt=""
                        className="w-14 h-14 rounded-xl border-2 border-white object-cover"
                      />
                    ))}
                  </div>
                  {/* body */}
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="font-heading font-bold text-stone-900 text-sm">{o.order_number}</span>
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${statusClass}`}>
                        {status.replace("_", " ")}
                      </span>
                    </div>
                    <div className="text-[11px] text-stone-500">
                      {new Date(o.created_at).toLocaleDateString()} • {o.items?.length || 0} item
                      {(o.items?.length || 0) === 1 ? "" : "s"} • Rs. {(o.total_amount || 0).toLocaleString()}
                    </div>
                    <div className="text-[11px] text-stone-600 truncate">
                      {(o.items || []).map((it) => it.product_name).join(", ")}
                    </div>
                  </div>
                  {/* actions */}
                  <div className="flex sm:flex-col items-end gap-2 justify-end">
                    <Link
                      to="/track-order"
                      state={{ prefill: { order_number: o.order_number, mobile: o.mobile_number } }}
                      data-testid={`my-order-track-${o.order_number}`}
                      className="text-[11px] font-bold text-[#996515] hover:text-[#1C1917] inline-flex items-center gap-1"
                    >
                      Track <ChevronRight className="w-3 h-3" />
                    </Link>
                    <a
                      href={`https://wa.me/?text=Order%20${encodeURIComponent(o.order_number)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                    >
                      <MessageCircle className="w-3 h-3" /> Contact
                    </a>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
